/**
 * Left rail: jobs (tokens, liquidity chains, hook chains) with sub-views,
 * saved views for the current job, and shortcuts to global views. Tokens
 * form a tree (TokenTree): clicking a token folds or unfolds its pages, so
 * any token's page is two clicks away.
 *
 * Desktop: a fixed-width column that collapses to an icon strip (persisted).
 * Narrow viewports (< 900px): nothing in flow; the TopBar hamburger opens
 * the same nav as an overlay drawer that closes on navigation.
 */
import { NavLink } from "react-router";
import { useJobs } from "@/api/hooks";
import { useAlertsSummary } from "@/views/alerts/hooks";
import type { ChainJob, TokenJob } from "@/api/types";
import { routes, syncState } from "@/lib/jobs";
import { useCurrentJob } from "@/state/job";
import { useUi } from "@/state/ui";
import { SavedViews } from "@/views/family/SavedViews";
import { RailFlyoutProvider } from "./RailFlyout";
import { linkCls, rowCls } from "./railStyles";
import { TokenTree } from "./TokenTree";

export function LeftRail() {
  const collapsed = useUi((s) => s.railCollapsed);
  const narrow = useUi((s) => s.narrow);
  const railOpen = useUi((s) => s.railOpen);
  const setRailOpen = useUi((s) => s.setRailOpen);
  const toggle = useUi((s) => s.toggleRail);
  if (narrow) {
    if (!railOpen) return null;
    return (
      <div className="fixed inset-0 z-40 flex" role="presentation">
        <div className="absolute inset-0 bg-black/40" onClick={() => setRailOpen(false)} aria-hidden />
        <aside
          className="relative flex h-full w-[min(var(--drawer-w),85vw)] shrink-0 flex-col overflow-hidden border-r border-border bg-bg-elev shadow-xl"
          role="dialog"
          aria-label="navigation"
          // any link click inside the drawer navigates, so close it; a token's
          // header is not a link (it folds the token), so folding keeps it open
          onClick={(e) => {
            if ((e.target as HTMLElement).closest("a")) setRailOpen(false);
          }}
        >
          <RailHeader onToggle={() => setRailOpen(false)} glyph="×" title="close menu" />
          <RailNav />
        </aside>
      </div>
    );
  }
  if (collapsed) {
    return (
      <aside className="flex w-6 shrink-0 flex-col items-center border-r border-border bg-bg-elev">
        <button
          type="button"
          className="mt-1 text-fg-faint hover:text-fg"
          onClick={toggle}
          title="expand rail"
          aria-label="expand rail"
        >
          ›
        </button>
      </aside>
    );
  }
  return (
    <aside className="flex w-[var(--rail-w)] shrink-0 flex-col overflow-hidden border-r border-border bg-bg-elev">
      <RailHeader onToggle={toggle} glyph="‹" title="collapse rail" />
      <RailNav />
    </aside>
  );
}

function RailHeader({ onToggle, glyph, title }: { onToggle: () => void; glyph: string; title: string }) {
  return (
    <div className="flex h-[var(--topbar-h)] shrink-0 items-center justify-between border-b border-border px-2">
      <span className="font-semibold tracking-wide">flow-web</span>
      <button
        type="button"
        className="px-1 text-fg-faint hover:text-fg"
        onClick={onToggle}
        title={title}
        aria-label={title}
      >
        {glyph}
      </button>
    </div>
  );
}

function RailNav() {
  const jobs = useJobs();
  const current = useCurrentJob();
  const d = jobs.data?.data;
  return (
    <RailFlyoutProvider>
      {/* data-rail: the global accent link colour leaves these links alone (styles/index.css) */}
      <nav className="min-h-0 flex-1 overflow-y-auto py-1 text-[11px]" data-rail>
        <NavLink to={routes.home()} end className={linkCls}>
          all tokens
        </NavLink>
        <RailHeading>tokens</RailHeading>
        {jobs.isLoading && <div className="px-2 text-fg-faint">loading…</div>}
        {jobs.isError && <div className="px-2 text-danger">jobs unavailable</div>}
        {d?.tokens.length === 0 && <div className="px-2 text-fg-faint">no token jobs</div>}
        {d && (
          <TokenTree
            label="tokens"
            jobs={d.tokens.filter((j) => syncState(j.tracking).syncing)}
            activeId={current.id}
          />
        )}
        <StoppedTokens
          jobs={d?.tokens.filter((j) => !syncState(j.tracking).syncing) ?? []}
          activeId={current.id}
        />
        <RailHeading>liquidity</RailHeading>
        {d?.liquidity.map((j) => (
          <ChainEntry key={j.id} job={j} active={current.id === j.id} />
        ))}
        {d?.liquidity.length === 0 && <div className="px-2 text-fg-faint">none</div>}
        <RailHeading>hooks</RailHeading>
        {d?.hooks.map((j) => (
          <ChainEntry key={j.id} job={j} active={current.id === j.id} />
        ))}
        {d?.hooks.length === 0 && (
          <div className="px-2 text-fg-faint" title="hooks.sqlite absent">
            none (no hooks.sqlite)
          </div>
        )}
        <RailHeading>global</RailHeading>
        <NavLink to={routes.signals()} className={linkCls}>
          signals
        </NavLink>
        <AlertsLink />
        <NavLink to={routes.positions()} className={linkCls}>
          positions
        </NavLink>
        <NavLink to={routes.health()} className={linkCls}>
          health
        </NavLink>
        <NavLink to={routes.compare()} className={linkCls}>
          compare <span className="text-fg-faint">(later)</span>
        </NavLink>
        <SavedViews jobId={current.id} />
      </nav>
    </RailFlyoutProvider>
  );
}

/** `alerts`, with the unread count when there is one. */
function AlertsLink() {
  const summary = useAlertsSummary();
  const unread = summary.data?.data.unread ?? 0;
  return (
    <NavLink to={routes.alerts()} className={linkCls}>
      alerts
      {unread > 0 && (
        <span
          className={`num ml-auto flex-none ${summary.data?.data.urgent_unread ? "text-danger" : "text-accent"}`}
          title={`${unread} unread`}
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </NavLink>
  );
}

/**
 * Tokens token-flow no longer follows, below the live ones and collapsed by
 * default: their history stays one click away without crowding the list.
 * The token being viewed stays visible either way.
 */
function StoppedTokens({ jobs, activeId }: { jobs: TokenJob[]; activeId: string | undefined }) {
  const open = useUi((s) => s.stoppedJobsOpen);
  const toggle = useUi((s) => s.toggleStoppedJobs);
  if (jobs.length === 0) return null;
  const shown = open ? jobs : jobs.filter((j) => j.id === activeId);
  return (
    <div className="mt-1">
      <button
        type="button"
        className={`${rowCls} w-full text-left text-fg-faint`}
        onClick={toggle}
        aria-expanded={open}
        title={open ? "hide tokens that are not syncing" : "show tokens that are not syncing"}
      >
        <span aria-hidden className="inline-block w-2.5 flex-none">
          {open ? "▾" : "▸"}
        </span>
        not syncing <span className="num">({jobs.length})</span>
      </button>
      <TokenTree label="tokens not syncing" jobs={shown} activeId={activeId} />
    </div>
  );
}

function RailHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-2 mb-0.5 px-2 text-3xs font-semibold uppercase tracking-wider text-fg-faint">
      {children}
    </div>
  );
}

function ChainEntry({ job, active }: { job: ChainJob; active: boolean }) {
  const to = job.kind === "liquidity" ? routes.ledgers(job.chain) : routes.hooks(job.chain);
  return (
    <NavLink
      to={to}
      className={linkCls}
      title={job.available ? `${job.kind} · ${job.chain}` : "store missing"}
      aria-current={active ? "page" : undefined}
    >
      {job.chain}
      {!job.available && <span className="text-warn">(missing)</span>}
    </NavLink>
  );
}
